import { type FC, useMemo } from 'react';

import { RenderingProvider, useCms, useContent, useSetting } from '@eneco/cms';
import type { CustomerReviewContent } from '@eneco/cms-types/generated/CustomerReview';
import RichText from '@eneco/components/RichText/RichText';
import { SpanHeadingS } from '@eneco/custom-components/CustomerReview';
import { useCustomerPublicGetCustomerRatings } from '@eneco/dc/hooks';
import { useFormatter } from '@eneco/i18n';
import { useLinkComponent } from '@eneco/link';
import { Bleed, Box, Card, Grid, Heading, Hidden, RatingStar, Stack, Stretch, Text, TextLink } from '@eneco/sparky';
import type { HeadingProps } from '@eneco/sparky/types';

interface CustomerReviewProps {
  componentName?: string;
  uid?: string;
  fields?: Record<string, unknown>;
  params?: Record<string, unknown>;
  placeholders?: Record<string, unknown>;
}

export const CustomerReviewInner: FC = () => {
  const Link = useLinkComponent();
  const { adapter } = useCms();
  const content = useContent<CustomerReviewContent>();
  const { data } = useCustomerPublicGetCustomerRatings(content.getLiveRatingDataOption === true ? undefined : null);
  const { format, number } = useFormatter();

  const ratingNumber = content.getLiveRatingDataOption ? data?.averageScore : Number(content.ratingNumber);
  const starRating = ratingNumber && Math.round(ratingNumber / 2);

  const headingLevel = (useSetting('headingLevel') ?? 'h2') as HeadingProps['as'];
  const intro = adapter.toRichText(content.content);
  const { link } = content;

  return (
    <Grid columns={{ md: '2', lg: '3' }} gap="16">
      <Grid.Item>
        <Stack gap="6" alignY={{ sm: 'justify' }}>
          {content.title && (
            <Heading as={headingLevel} size={{ initial: 'S', md: 'M' }} color="inherit">
              {content.title}
            </Heading>
          )}
          {intro && (
            <Text size={{ initial: 'BodyM', md: 'BodyL' }}>
              <RichText html={intro} />
            </Text>
          )}
          <Stack.Item>
            <SpanHeadingS>
              {ratingNumber && starRating && (
                <Stack direction="row" alignY="end" gap="2">
                  <Text>{number.oneFractionDigit(ratingNumber).replace('.', ',')} </Text>
                  <RatingStar maxStars={5} score={ratingNumber} size="large" />
                </Stack>
              )}
            </SpanHeadingS>
            {content.ratingSubText && link?.href && (
              <Text size={{ initial: 'BodyS' }}>
                {format(content.ratingSubText, { numberOfReviews: data?.ratingsAmount || '' })}{' '}
              </Text>
            )}
          </Stack.Item>

          {link?.text && (
            <Box>
              <Link linkType={link.linkType ?? 'external'} linkValue={{ href: link.href, target: link.target }}>
                <TextLink emphasis="high">{link.text}</TextLink>
              </Link>
            </Box>
          )}
        </Stack>
      </Grid.Item>
      {(content.items ?? []).map((item, index) => {
        const maxStars = 5;
        const reviewText = adapter.toRichText(item.content);
        const itemRating = Number(item.ratingNumber);
        // Sanity items carry no id; the position is stable for a fixed list.
        const itemKey = `${item.itemName ?? 'review'}-${index}`;
        const review = (
          <Bleed top="8" key={itemKey}>
            <Stretch>
              <Grid.Item>
                <Stretch>
                  <Grid gridTemplateColumns="auto auto 24px" gridTemplateRows="32px auto">
                    <Grid.Item gridColumn="1/-1" gridRow="2">
                      <Stretch>
                        <Card>
                          <Box padding={{ initial: '5', md: '10' }} paddingTop="10">
                            <Stack gap="6">
                              {reviewText && (
                                <Text size={{ initial: 'BodyM', md: 'BodyL' }}>
                                  <RichText html={reviewText} />
                                </Text>
                              )}
                              <Stack.Item>
                                {item.title && (
                                  <Text size={{ initial: 'BodyS', md: 'BodyM' }} color="textLowEmphasis">
                                    {item.title}
                                  </Text>
                                )}
                                {item.ratingNumber && (
                                  <Stack direction="row" alignY="start" gap="2">
                                    <Text weight="bold" size="BodyL">
                                      {Math.round(itemRating)}/{maxStars * 2}
                                    </Text>
                                    <RatingStar maxStars={maxStars} score={itemRating} size="medium" />
                                  </Stack>
                                )}
                              </Stack.Item>
                            </Stack>
                          </Box>
                        </Card>
                      </Stretch>
                    </Grid.Item>
                  </Grid>
                </Stretch>
              </Grid.Item>
            </Stretch>
          </Bleed>
        );

        return index === 1 ? (
          <Hidden below="lg" key={itemKey}>
            {review}
          </Hidden>
        ) : (
          review
        );
      })}
    </Grid>
  );
};

const CustomerReview: FC<CustomerReviewProps> = ({ fields, params, placeholders, componentName, uid }) => {
  const { adapter } = useCms();
  const rendering = useMemo(
    () =>
      adapter.toRendering({
        componentName: componentName ?? 'CustomerReview',
        uid,
        fields,
        params,
        placeholders,
      }),
    [adapter, fields, params, placeholders, componentName, uid],
  );

  return (
    <RenderingProvider rendering={rendering}>
      <CustomerReviewInner />
    </RenderingProvider>
  );
};

export default CustomerReview;
